import React, { useEffect, useMemo, useState } from 'react';
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { Box, CircularProgress, Grid } from '@mui/material';
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { injectVfsTimesNewRoman } from '../../../functions/pdfMake/timesNewRomanFonts';
import { formatDate2, formatDateAsDate } from '../../../functions/formatters';
import { asJSONSafe } from '../../../functions';
import { serviceGroupCRUD } from '../../../http';
import { normalizeIndicators } from '../../../functions/normalization';

pdfMake.vfs = pdfFonts.pdfMake.vfs;
injectVfsTimesNewRoman(pdfMake.vfs);

const style = {
    position: 'absolute',
    top: '10%',
    bottom: '20%',
    left: '20%',
    right: '20%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    overflow: 'auto',
    height: '85%',
    p: 4,
};

function ResolutionPdf({ title, data, open, setOpen, onClose }) {    
    const [dataUrl, setDataUrl] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetch = async () => {
        setLoading(true);

        console.log(data);

        data.standards = asJSONSafe(data.standards);
        data.regulationOfCUs = asJSONSafe(data.regulationOfCUs);        
        data.indicators = normalizeIndicators(asJSONSafe(data.indicators));

        const indicatorsBody = [];

        for (let i = 0; i < data.indicators.length; i++) {
            const group = data.indicators[i];

            let groupId = group.groupId;

            if (!groupId) {
                groupId = group.indicators[0].groupId;
            }

            const groupRecord = await serviceGroupCRUD.get(groupId);
            
            // Добавляем строку с названием группы и colSpan
            indicatorsBody.push([{ text: groupRecord.name, style: 'tableData', colSpan: 2 }]);
          
            // Проходим по каждому индикатору в текущей группе
            for (let j = 0; j < group.indicators.length; j++) {
              const indicator = group.indicators[j];
          
              // Добавляем строку для каждого индикатора
              indicatorsBody.push([
                { text: indicator.name, style: 'tableData' },  // Преобразуем значение в строку
                { text: indicator.checked ? 'Да' : 'Нет', style: 'tableData' },  // Преобразуем булево значение в строку
              ]);
            }
          }

        const docDefinition = {
            watermark: { angle: -70, text: 'Қазақстан Республикасы Денсаулық сақтау министрлігі Санитариялық-эпидемиологиялық бақылау комитетінің «Ұлттық сараптама орталығы» шаруашылық жүргізу құқығындағы республикалық мемлекеттік кәсіпорны', color: 'gray', opacity: 0.3, bold: true, italics: false, fontSize: 20 },
            info: {
                title: 'Постановление',
                author: 'NCE',
                subject: 'Resolution'
            },
            pageOrientation: 'portrait',
            pageSize: 'A4',
            pageMargins: [56, 28, 28, 28],
            defaultStyle: {
                font: 'TimesNewRoman',
            },
            content:
                [
                    {
                        table: {
                            // headers are automatically repeated if the table spans over multiple pages
                            // you can declare how many rows should be treated as headers
                            headerRows: 1,
                            widths: ['*', '*'],
                            body: [
                                [{
                                    text: "Приложение 3 к правилам проведения санитарно-эпидемиологической экспертизы",
                                    colSpan: 2,
                                    border: [false, false, false, false],
                                    alignment: 'right'
                                }, ""],
                                [{
                                    text: "Форма Постановление о проведении санитарно-эпидемиологической экспертизы",
                                    colSpan: 2,
                                    border: [false, false, false, false],
                                    alignment: 'right'
                                }, ""]
                            ]
                        }
                    },
                    {
                        table: {
                            // headers are automatically repeated if the table spans over multiple pages
                            // you can declare how many rows should be treated as headers
                            headerRows: 1,
                            widths: ['*', '*'],
                            body: [                                
                                [{
                                    text: `Филиал: ${data?.sourceFilial?.name}`}, `Районное отделение: ${data?.sourceFilialDivision?.name}`],
                                [`Номер: ${data?.number}`, `Дата: ${formatDateAsDate(data?.createdAt)}`],
                                [{
                                    text: `Дата назначения: ${formatDateAsDate(data?.targetDate)}`,
                                    colSpan: 2
                                }, ''],
                                [{
                                    text: `Наименование проверяемого субъекта`                                    
                                }, data?.targetObject?.name || ''],
                                [
                                    `Должностное лицо санитарно-эпидемиологической службы`, data?.responsibleFullName || ''
                                ]
                            ]
                        }
                    },
                    {
                        text: 'Рассмотрев представленные документы:',
                        style: 'header'
                    },
                    {
                        table: {
                            headerRows: 1,
                            widths: ['auto', '*', '*'],
                            body: [
                                // Заголовок таблицы
                                [
                                    { text: '№ n/n', style: 'tableHeader', alignment: 'center' },
                                    { text: 'Номер акта', style: 'tableHeader', alignment: 'center' },
                                    { text: 'Дата создания', style: 'tableHeader', alignment: 'center' },
                                ],
                                // Данные актов
                                ...(data?.acts?.length
                                    ? data.acts.map((_, index) => [
                                          { text: index + 1, style: 'tableData', alignment: 'center' },
                                          { text: _.act?.number || '-', style: 'tableData', alignment: 'center' },
                                          { text: formatDateAsDate(_.act?.createdAt) || '-', style: 'tableData', alignment: 'center' },
                                      ])
                                    : [
                                          [
                                              {
                                                  text: 'Нет добавленных актов.',
                                                  colSpan: 3,
                                                  alignment: 'center',
                                                  style: 'tableData',
                                              },
                                              '',
                                              '',
                                          ],
                                      ]),
                            ],
                        },
                        layout: 'lightHorizontalLines'
                    },
                    {
                        table: {
                            headerRows: 1,
                            widths: ['*', '*'],
                            body: [
                                [
                                    {
                                        text: `Установил (указать характер складывающейся
                                санитарно-эпидемиологической ситуации на
                                территории, группе объектов, нарушения законодательства Республики Казахстан в области
                                санитарно-эпидемиологического благополучия населения)`
                                    },
                                    {
                                        text: data?.establishedSituationDescription || ''
                                    }
                                ],
                                [
                                    {
                                        text: 'Основание вынесения Постановления'
                                    },
                                    {
                                        text: data?.purpose?.name || data?.purposeOfSelection?.name || ''
                                    }
                                ],
                                [
                                    {
                                        text: 'Создал заявку'
                                    },
                                    {
                                        text: data?.createdBy?.name || ''
                                    }
                                ],
                                [
                                    {
                                        text: 'Постановил',
                                        colSpan: 2
                                    },
                                    {
                                        text: ''
                                    }
                                ],
                                [{
                                    text: `Филиал: ${data?.filial?.name}`}, `Районное отделение: ${data?.filialDivision?.name}`],
                            ],
                        }
                    },
                    {
                        text: 'Документ санитарно-эпидемиологического нормирования (на соответствие, которого проводится исследование (ГОСТ):',
                        style: 'header',
                        margin: [0, 10],
                    },
                    {
                        table: {
                            headerRows: 1,
                            widths: ['auto', '*'],
                            body: [
                                // Заголовок таблицы
                                [
                                    { text: '№', style: 'tableHeader', alignment: 'center' },
                                    { text: 'Наименование ГОСТ', style: 'tableHeader', alignment: 'center' },
                                ],
                                // Данные ГОСТ
                                ...(data?.standards?.length
                                    ? data.standards.map((standard, index) => [
                                          { text: index + 1, style: 'tableData', alignment: 'center' },
                                          { text: standard.name || '-', style: 'tableData', alignment: 'left' },
                                      ])
                                    : [
                                          [
                                              {
                                                  text: 'Нет выбранных ГОСТ.',
                                                  colSpan: 2,
                                                  alignment: 'center',
                                                  style: 'tableData',
                                              },
                                              '',
                                          ],
                                      ]),
                            ],
                        },
                        layout: 'lightHorizontalLines',
                        margin: [0, 5],
                    },
                    {
                        text: 'Документ санитарно-эпидемиологического нормирования (на соответствие, которого проводится исследование (ТР/ТС):',
                        style: 'header',
                        margin: [0, 10],
                    },
                    {
                        table: {
                            headerRows: 1,
                            widths: ['auto', '*'],
                            body: [
                                // Заголовок таблицы
                                [
                                    { text: '№', style: 'tableHeader', alignment: 'center' },
                                    { text: 'Наименование ТР/ТС', style: 'tableHeader', alignment: 'center' },
                                ],
                                // Данные ТР/ТС
                                ...(data?.regulationOfCUs?.length
                                    ? data.regulationOfCUs.map((regulation, index) => [
                                          { text: index + 1, style: 'tableData', alignment: 'center' },
                                          { text: regulation.name || '-', style: 'tableData', alignment: 'left' },
                                      ])
                                    : [
                                          [
                                              {
                                                  text: 'Нет выбранных ТР/ТС.',
                                                  colSpan: 2,
                                                  alignment: 'center',
                                                  style: 'tableData',
                                              },
                                              '',
                                          ],
                                      ]),
                            ],
                        },
                        layout: 'lightHorizontalLines',
                        margin: [0, 5],
                    },
                    {
                        text: 'Показатели:',
                        style: 'bigHeader',
                        margin: [0, 20]
                    },
                    // Генерация таблицы для показателей
                    {
                        table: {
                            headerRows: 1,
                            widths: ['*', 'auto'],
                            body: [
                                // Заголовки таблицы
                                [{
                                    text: 'Название показателя',
                                    style: 'tableHeader'
                                }, {
                                    text: 'Выбор',
                                    style: 'tableHeader'
                                }],
                                // Данные из массива indicators
                                ...indicatorsBody
                            ]
                        },
                        style: 'table'
                    },
                ],
            styles: {
                header: {
                    alignment: 'center',
                    margin: [0, 5],
                    fontSize: 11,
                    bold: true
                },
                bigHeader: {
                    alignment: 'center',
                    margin: [0, 0, 0, 10],
                    fontSize: 16,
                    bold: true
                },
                table: {
                    fontSize: 5,
                    margin: [0, 0, 10, 0],
                    border: '5px'
                },
                td: {
                    size: 1
                },
                text: {
                    fontSize: 9,
                    margin: [0, 0, 0, 0],
                },
                tableHeader: {
                    fontSize: 14,
                    bold: true,
                    margin: [0, 5, 0, 5],
                },
                tableData: {
                    fontSize: 14,
                    margin: [0, 2, 0, 2],
                },
            },        
        };

        pdfMake.fonts = {
            // All 4 components must be defined
            TimesNewRoman: {
                normal: 'Times-Regular.ttf',
                bold: 'Times-Bold.ttf',
                italics: 'Times-Italic.ttf',
                bolditalics: 'Times-BoldItalic.ttf'
            },
            Roboto: {
                normal: 'Roboto-Regular.ttf',
                bold: 'Roboto-Medium.ttf',
                italics: 'Roboto-Italic.ttf',
                bolditalics: 'Roboto-MediumItalic.ttf'
            }
        };
        // pdfMake.fonts.TimesNewRoman = {
        //     normal: 'Times-New-Roman-Regular.ttf',
        //     bold: 'Times-New-Roman-Bold.ttf',
        //     italics: 'Times-New-Roman-Italic.ttf',
        //     bolditalics: 'Times-New-Roman-BoldItalic.ttf'
        // };

        const pdfDocGenerator = pdfMake.createPdf(docDefinition);
        // pdfDocGenerator.download(_ => {
        //
        // });
        pdfDocGenerator.getDataUrl((dataUrl) => {
            // const blob = b64toBlob(dataUrl, 'application/pdf');
            // const blobUrl = URL.createObjectURL(blob);

            // window.location = blobUrl;
            setDataUrl(dataUrl);
        });

        setLoading(false);
    }

    useEffect(() => {
        fetch();
    }, [data]);

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description">
            <Box sx={style}>
                <Grid container spacing={2} direction='column'>
                {!loading
                ? <>
                    <Grid item>
                        <Typography id="modal-modal-title" variant="h6" component="h2">
                            {title}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <iframe src={dataUrl} style={{ width: '100%', minHeight: '900px' }}></iframe>
                    </Grid>
                </>
                : <Grid item><CircularProgress size={200} /></Grid>}
                </Grid>            
            </Box>            
        </Modal>
    )
}

export default ResolutionPdf;
